import React, { type FC, useState, useCallback, useRef, useMemo } from 'react';
import { injectIntl, type WithIntlProps, type WrappedComponentProps } from 'react-intl';

import { Box } from '@atlaskit/primitives/compiled/box';

import { type WorkflowStatus } from '@confluence/approvals/entry-points/ApprovalsContext.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { ContentState } from '../types.ts';
import type { ContentStateStateContainer } from '../ContentStateStateContainer.tsx';

import { ContentStateMenuV2SelectView } from './ContentStateMenuV2SelectView.tsx';
import { ContentStateMenuV2CreateView } from './ContentStateMenuV2CreateView.tsx';
import { ContentStateMenuV2DisabledView } from './ContentStateMenuV2DisabledView.tsx';
import { i18n } from './contentStateMenuV2I18n.ts';

export type DefaultSectionItem =
	| { type: 'contentState'; contentState: ContentState }
	| { type: 'workflowStatus'; workflowStatus: WorkflowStatus }
	| { type: 'approvalUpsell' };

enum ConstentStateMenuViewTypes {
	SELECT = 'select',
	CREATE = 'create',
}

const getDefaultSectionItemName = (
	item: DefaultSectionItem,
	approvalUpsellLabel: string,
): string | null | undefined => {
	if (item.type === 'contentState') {
		return item.contentState.name;
	}

	if (item.type === 'workflowStatus') {
		if (item.workflowStatus.allowsReviews) {
			return `${approvalUpsellLabel} ${item.workflowStatus.name}`;
		}

		return item.workflowStatus.name;
	}

	return approvalUpsellLabel;
};

const isApprovalFooterItem = (item: DefaultSectionItem) =>
	item.type === 'approvalUpsell' ||
	(item.type === 'workflowStatus' && item.workflowStatus.allowsReviews);

export type ContentStateMenuV2Props = WrappedComponentProps & {
	contentId: string;
	contentStateState: ContentStateStateContainer;
	contentType: 'page' | 'blogpost' | null;
	isLivePage: boolean;
	defaultStatesArray: (ContentState | null)[];
	customStatesArray: (ContentState | null)[];
	isContentStateEnabled: boolean;
	userOffline: boolean;
	onSelectExistingStatus: (selectedStatus: ContentState) => void;
	onSaveCustomStatus: (statusName: string, colorId: string) => void;
	onDeleteCustomStatus: (status: ContentState) => void;
	onRemove: () => void;
	isContentStateMenuOpen: boolean;
	setIsContentStateMenuOpen: (newValue: boolean) => void;
	workflowStatuses: WorkflowStatus[];
	onSelectWorkflowStatus: (status: WorkflowStatus) => void;
	showApprovalsUpsell?: boolean;
	onSelectApprovalsUpsell?: () => void;
};

const ContentStateMenuV2Component: FC<ContentStateMenuV2Props> = ({
	contentStateState,
	contentType,
	isLivePage,
	defaultStatesArray,
	customStatesArray,
	onSelectExistingStatus,
	onSaveCustomStatus,
	onDeleteCustomStatus,
	onRemove,
	setIsContentStateMenuOpen,
	workflowStatuses = [],
	onSelectWorkflowStatus,
	showApprovalsUpsell = false,
	onSelectApprovalsUpsell,
	intl: { formatMessage },
}) => {
	const { edition } = useSessionData();
	const isPremium = edition === ConfluenceEdition.PREMIUM;

	const [searchValue, setSearchValue] = useState<string>('');
	const [currentView, setCurrentView] = useState<ConstentStateMenuViewTypes>(
		ConstentStateMenuViewTypes.SELECT,
	);
	const menuRef = useRef<HTMLDivElement | null>(null);

	const validDefaultStates = useMemo(
		() => (defaultStatesArray || []).filter((s): s is ContentState => s !== null),
		[defaultStatesArray],
	);

	const validCustomStates = useMemo(
		() => (customStatesArray || []).filter((s): s is ContentState => s !== null),
		[customStatesArray],
	);

	const mergedDefaultSection = useMemo((): DefaultSectionItem[] => {
		const contentStateItems: DefaultSectionItem[] = validDefaultStates.map((s) => ({
			type: 'contentState' as const,
			contentState: s,
		}));

		/**
		 * Whether the workflow-status section should appear in the menu.
		 * Live docs can show "In review" statuses, which then trigger the convert-to-page popup.
		 */
		const shouldShowWorkflowStatuses =
			contentType === 'page' &&
			workflowStatuses.length > 0 &&
			isPremium &&
			(!isLivePage || expValEquals('confluence_approvals_eap', 'isEnabled', true));
		const shouldShowApprovalsUpsell =
			contentType === 'page' &&
			showApprovalsUpsell &&
			(!isLivePage || expValEquals('confluence_approvals_eap', 'isEnabled', true));

		if (!shouldShowWorkflowStatuses && !shouldShowApprovalsUpsell) {
			return contentStateItems;
		}

		const workflowItems: DefaultSectionItem[] = shouldShowWorkflowStatuses
			? workflowStatuses.map((s) => ({
					type: 'workflowStatus' as const,
					workflowStatus: s,
				}))
			: [];
		const approvalUpsellItems: DefaultSectionItem[] = shouldShowApprovalsUpsell
			? [{ type: 'approvalUpsell' }]
			: [];

		const restrictedIndex = contentStateItems.findIndex(
			(item) => item.type === 'contentState' && item.contentState.restrictionLevel !== 'NONE',
		);

		if (restrictedIndex !== -1) {
			const result = [...contentStateItems];
			result.splice(restrictedIndex, 0, ...workflowItems);
			return [...result, ...approvalUpsellItems];
		}
		return [...contentStateItems, ...workflowItems, ...approvalUpsellItems];
	}, [
		contentType,
		isLivePage,
		isPremium,
		showApprovalsUpsell,
		validDefaultStates,
		workflowStatuses,
	]);

	const [selectedContentState, setSelectedContentState] = useState<ContentState | null>(null);
	const currentContentState = selectedContentState ?? contentStateState.state.contentState;

	const handleSelectStatus = useCallback(
		(selectedStatus: ContentState) => {
			if (currentContentState?.id === selectedStatus.id) {
				setSelectedContentState(null);
				onRemove();
				setIsContentStateMenuOpen(false);
				setSearchValue('');
				return;
			}
			setSelectedContentState(selectedStatus);
			onSelectExistingStatus(selectedStatus);
			setIsContentStateMenuOpen(false);
			setSearchValue('');
		},
		[onSelectExistingStatus, onRemove, currentContentState, setIsContentStateMenuOpen],
	);

	const handleSelectApprovalsUpsell = useCallback(() => {
		setIsContentStateMenuOpen(false);
		setSearchValue('');
		onSelectApprovalsUpsell?.();
	}, [onSelectApprovalsUpsell, setIsContentStateMenuOpen]);

	const filteredDefaultSection = useMemo((): DefaultSectionItem[] => {
		if (!searchValue.trim()) return mergedDefaultSection;
		const lowerQuery = searchValue.toLowerCase();
		const approvalUpsellLabel = formatMessage(i18n.startApproval);
		return mergedDefaultSection.filter((item) => {
			const name = getDefaultSectionItemName(item, approvalUpsellLabel);
			return name?.toLowerCase().includes(lowerQuery);
		});
	}, [formatMessage, mergedDefaultSection, searchValue]);

	const hasApprovalFooterOption = useMemo(
		() => mergedDefaultSection.some(isApprovalFooterItem),
		[mergedDefaultSection],
	);

	const filteredCustomOptions = useMemo(() => {
		if (!searchValue.trim()) return validCustomStates;
		const lowerQuery = searchValue.toLowerCase();
		return validCustomStates.filter((option) => option.name?.toLowerCase().includes(lowerQuery));
	}, [validCustomStates, searchValue]);

	const hasResults = filteredDefaultSection.length > 0 || filteredCustomOptions.length > 0;

	const handleAddCustomClick = useCallback(() => {
		setCurrentView(ConstentStateMenuViewTypes.CREATE);
	}, []);

	const handleBackToSearch = useCallback(() => {
		setCurrentView(ConstentStateMenuViewTypes.SELECT);
	}, []);

	const handleSaveCustomStatus = useCallback(
		(statusName: string, colorId: string) => {
			onSaveCustomStatus(statusName, colorId);
			setIsContentStateMenuOpen(false);
			setCurrentView(ConstentStateMenuViewTypes.SELECT);
			setSearchValue('');
		},
		[onSaveCustomStatus, setIsContentStateMenuOpen],
	);

	if (!contentStateState.state.isContentStateEnabled) {
		return (
			<Box ref={menuRef} testId="content-state-menu-v2">
				<ContentStateMenuV2DisabledView />
			</Box>
		);
	}

	if (currentView === ConstentStateMenuViewTypes.CREATE) {
		return (
			<Box ref={menuRef}>
				<ContentStateMenuV2CreateView
					initialValue={searchValue}
					onBack={handleBackToSearch}
					onSave={handleSaveCustomStatus}
				/>
			</Box>
		);
	}

	return (
		<Box ref={menuRef} testId="content-state-menu-v2">
			<ContentStateMenuV2SelectView
				searchValue={searchValue}
				onSearchChange={setSearchValue}
				filteredDefaultSection={filteredDefaultSection}
				filteredCustomOptions={filteredCustomOptions}
				hasResults={hasResults}
				hasApprovalFooterOption={hasApprovalFooterOption}
				isCustomEnabled={contentStateState.state.isCustomEnabled}
				currentContentState={currentContentState}
				onSelectStatus={handleSelectStatus}
				onSelectWorkflowStatus={onSelectWorkflowStatus}
				onSelectApprovalsUpsell={handleSelectApprovalsUpsell}
				onAddCustomClick={handleAddCustomClick}
				onDeleteCustomStatus={onDeleteCustomStatus}
			/>
		</Box>
	);
};

export const ContentStateMenuV2: React.FC<WithIntlProps<ContentStateMenuV2Props>> & {
	WrappedComponent: React.ComponentType<ContentStateMenuV2Props>;
} = injectIntl(ContentStateMenuV2Component);
